import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { filterEmpty, getTableScrollStyle } from './libs/basic';
import { ITable, TableExtraAction } from './model/table';
import { TableBody } from './table-body';
import { TableHeader } from './table-header';
import './style.scss';
import './styles/index';
import { PaginationComponent } from './components/pagination';
import {
  renderBodyLineTRSlot,
  renderEmpty,
  TableTitleComponent,
} from './components';
import {
  IPagination,
  ITableColumn,
  ITableSortValue,
  TableExpandKeys,
  TableTreeExpand,
} from './model';
import {
  getCurrentPageData,
  getCurrentPaginationStartNumber,
  getFilterData,
  localSortData,
  log,
} from './libs';
import { INumber, IString } from '../../model';
import { IncompleteMultiLevelHeaderComponent } from './components/table-header/incomplete-multi-level-header';
import { Spin } from 'antd';

export function Table<T>(props: ITable<T>) {
  const tableRef = useRef<HTMLTableElement | null>(null);
  const tableInnerRef = useRef<HTMLTableElement | null>(null);
  const { pagination, pageSizeOptions } = props;
  // 列表全部数据
  const [sourceList, setSourceList] = useState<ReadonlyArray<T>>(
    props.dataSource
  );

  // 数据缓存, 用作恢复过滤/排序之用
  const [backupFilterData, setBackupFilterData] = useState<ReadonlyArray<T>>(
    []
  );
  const [backupSortData, setBackupSortData] = useState<ReadonlyArray<T>>([]);

  // 排序
  const [sortValue, setSortValue] = useState<ITableSortValue | null>(
    props.defaultSort ? props.defaultSort : null
  );
  const [sortLoading, setSortLoading] = useState<boolean>(false);

  // 过滤项
  const [filterValues, setFilterValues] = useState<IString<string | string[]>>(
    {}
  );

  // 展开或者收起的树数据显示
  const [expandTree, setExpandTree] = useState<TableTreeExpand>({});

  // 详情展开或者收起
  const [expandedKeys, setExpandedKeys] = useState<TableExpandKeys>(
    props.expandDetail?.expandKeys || []
  );

  const [widths, setWidths] = useState<ReadonlyArray<number>>(
    new Array(props.columns.length)
  );
  const widthRef = useRef<Array<number>>(new Array(props.columns.length));

  const [isDragEnter, setIsDragEnter] = useState<boolean>(false);

  // 多级表头支持折叠和展开所覆盖的列，受控
  const [multiColumnCollapseIndex, setMultiColumnCollapseIndex] = useState<
    INumber<ReadonlyArray<number>>
  >(props.multiColumnCollapseIndex || {});

  useEffect(() => {
    setExpandedKeys(props.expandDetail?.expandKeys || expandedKeys);
  }, [props.expandDetail?.expandKeys]);

  const onChangedSortValue = (
    value: ITableSortValue | null,
    column: ITableColumn<T>
  ) => {
    onSortValue(value, column);
    onChangePagination(1);
  };

  const onChangeTreeExpand = (id: string, status: boolean) => {
    setExpandTree((prev) =>
      Object.assign({}, prev, {
        [id]: status,
      })
    );
  };

  const onChangeExpand = (id: string, open: boolean) => {
    let newKeys: string[] = [];
    if (open) {
      newKeys = [...expandedKeys, id];
    } else {
      newKeys = expandedKeys.filter((item) => item !== id);
    }
    setExpandedKeys(newKeys);
    props.expandDetail?.onChange?.(id, open, newKeys);
  };

  // 本地/远程排序
  // 本地只排序当前页面数据
  const onSortValue = async (
    value: ITableSortValue | null,
    column: ITableColumn<T>
  ) => {
    if (!value) {
      log.error('sort value empty', value);
      return;
    }

    setSortLoading(true);
    setSortValue(value);
    let sortData: T[] = [];

    // 是否启用汇总，如果启用汇总，汇总的数据不参与排序。
    // 排序的依据：理论上要根据 汇总的 show 状态判断，可是目前缺陷那边外包同学未使用表格自带的汇总计算方法，导致 show 状态无法判断
    const isSummary = !!props.summary?.summaryKeys;

    // 远程排序
    if (column.sortable === 'custom' && value) {
      if (column.sortRemote) {
        try {
          sortData = await column.sortRemote(value);
        } catch (err) {
          log.error('sort remote fail', err);
        }
      }
    }

    // 本地排序
    if (column.sortable === true) {
      let summaryItem: T[] = [];
      let backupData = backupSortData.length
        ? backupSortData
        : backupFilterData;
      let sortableList = sourceList.slice();
      if (isSummary) {
        sortableList = sourceList.slice(0, -1);
        summaryItem = sourceList.slice(-1);
        backupData = backupData.slice(0, -1);
      }
      sortData = localSortData<T>(sortableList, value, column, backupData);
      if (sortData.length) {
        setSourceList(sortData.concat(summaryItem));
      }
    }

    // 事件触发
    if (props.onChange) {
      props.onChange(pagination, null, value, {
        currentDataSource: sortData,
        action: TableExtraAction.sort,
      });
    }
    setSortLoading(false);
  };

  const onChangedFilterValues = (key: string, value: string | string[]) => {
    setFilterValues((prev) =>
      Object.assign({}, prev, {
        [key]: value,
      })
    );
    onChangePagination(1);
  };

  const onChangePagination = (
    page: number,
    pageSize?: number,
    total?: number,
    data?: ReadonlyArray<T>
  ) => {
    const paginationData: IPagination = Object.assign({}, pagination, {
      current: page,
      pageSize: pageSize !== undefined ? pageSize : pagination?.pageSize,
      total: total !== undefined ? total : pagination?.total,
    });

    if (props.onChange) {
      props.onChange(paginationData, null, null, {
        currentDataSource: data || sourceList,
        action: TableExtraAction.paginate,
      });
    }
  };

  const onWidthChanged = (index: number, width: number) => {
    widthRef.current[index] = width;
    setWidths([...widthRef.current]);
  };

  const onDrop = (ev: any) => {
    if (props.isDragging) {
      return;
    }
    setIsDragEnter(false);
    if (ev.dataTransfer) {
      if (props?.onSaveDropData) {
        props.onSaveDropData(ev.dataTransfer.getData('text/plain'));
      }
    }
  };

  const onDragStart = (ev: DragEvent) => {
    props?.onDraggingChange && props.onDraggingChange(true);
    if (ev.dataTransfer) {
      const selectedData = props.rowSelection?.selectedRowKeys.map(
        (v) => sourceList[+v]
      );
      ev.dataTransfer.setData('text/plain', JSON.stringify(selectedData));
    }
  };

  const onDragEnd = () => {
    props?.onDraggingChange && props.onDraggingChange(false);
  };

  const onDragOver = (e: any) => {
    if (
      getMouseInTable(e.clientX, e.clientY) &&
      !props.isDragging &&
      !isDragEnter
    ) {
      setIsDragEnter(true);
    }
    e.preventDefault();
  };

  const onDragLeave = (e: any) => {
    if (!getMouseInTable(e.clientX, e.clientY)) {
      setIsDragEnter(false);
    }
  };

  const getDragEnterStyle = () => {
    if (isDragEnter) {
      return {
        border: '1px solid var(--color-text-link)',
      };
    }
    return {};
  };

  const getMouseInTable = (clientX: number, clientY: number) => {
    if (tableInnerRef.current) {
      const { x, y, width, height } =
        tableInnerRef.current.getBoundingClientRect();
      if (
        clientX >= x &&
        clientX <= x + width &&
        clientY >= y &&
        clientY <= y + height
      ) {
        return true;
      }
      return false;
    }
    return false;
  };

  // 显示隐藏列的时候，重置resize状态
  useEffect(() => {
    widthRef.current = new Array(props.columns.length);
    setWidths([...widthRef.current]);
  }, [props.columns]);

  useEffect(() => {
    setSourceList(props.dataSource);
    setBackupFilterData(props.dataSource);
  }, [props.dataSource]);

  // 处理默认排序
  useEffect(() => {
    if (backupFilterData.length && sortValue?.key) {
      const column = props.columns.find((item) => item.key === sortValue.key);
      if (column) {
        // 接口排序默认不处理，接口排序的数据直接赋值
        if (column.sortable && column.sortable !== 'custom') {
          onSortValue(sortValue, column);
        }
      }
    }
  }, [backupFilterData]);

  // 当分页，变化后是否滚动到表格顶部
  useEffect(() => {
    if (!props.scroll) {
      return;
    }
    if (props.scroll.scrollToFirstRowOnChange) {
      tableRef.current?.scrollTo({
        top: 0,
      });
    }
  }, [props.scroll?.scrollToFirstRowOnChange, pagination?.current, tableRef]);

  // 处理表头过滤
  useEffect(() => {
    let data = backupFilterData;
    if (backupFilterData.length) {
      const values = filterEmpty(filterValues);
      if (Object.keys(values).length) {
        data = getFilterData<T>(backupFilterData, values, props.columns);
      }
      setSourceList(data);
      setBackupSortData(data);
    } else {
      setSourceList([]);
      setBackupSortData([]);
    }
  }, [filterValues, backupFilterData]);

  // 处理树展开变化
  useEffect(() => {
    if (props.tree?.expand) {
      setExpandTree(props.tree.expand);
    }
  }, [props.tree?.expand]);

  const currentList = getCurrentPageData<T>(sourceList, pagination);
  const startIndex = getCurrentPaginationStartNumber(
    sourceList.length,
    pagination
  );
  const renderBodyContent = () => {
    const rowSelectionLen = props.rowSelection ? 1 : 0;
    const emptyLen = props.columns.length + rowSelectionLen;

    const getTableBody = () => {
      if (sourceList.length) {
        // eslint-disable-next-line
        const isEnableRowDarg = !!(props?.isEnableRowDrag && !props?.loading);
        return (
          <TableBody<T>
            startIndex={startIndex?.start || 0}
            rowSelection={props.rowSelection}
            columns={props.columns}
            fullData={sourceList}
            data={currentList}
            rowClassName={props.rowClassName}
            summary={props.summary}
            onRow={props.onRow}
            rowKey={props.rowKey}
            tree={props.tree}
            expandTree={expandTree}
            onChangeTreeExpand={onChangeTreeExpand}
            isEnableResize={
              props?.isEnableResize === true ? props.isEnableResize : false
            }
            isEnableRowDrag={isEnableRowDarg}
            columnWidths={widths}
            expandDetail={props.expandDetail}
            expandedKeys={expandedKeys}
            onChangeExpand={onChangeExpand}
            columnCollapseIndex={multiColumnCollapseIndex}
            renderIconDown={props?.renderIconDown}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            isDragging={props.isDragging}
            highlightStyle={props.highlightStyle}
            tableRefElement={tableRef.current || undefined}
          />
        );
      }
      return null;
    };

    if (sourceList.length) {
      return getTableBody();
    }
    if (props.loading) {
      return renderBodyLineTRSlot(
        <div style={{ height: 150 }}></div>,
        emptyLen
      );
    }
    if (props.showDefaultEmpty !== false) {
      return renderBodyLineTRSlot(
        renderEmpty(props.locale?.emptyText),
        emptyLen
      );
    }

    // 添加自定义空状态
    if (props.showDefaultEmpty === false && props?.renderEmpty) {
      return renderBodyLineTRSlot(
        props.renderEmpty(props.locale?.emptyText),
        emptyLen
      );
    }
    return null;
  };

  const renderFooter = () => {
    if (props.renderFooter) {
      return <div className="table-footer">{props.renderFooter()}</div>;
    }
    return null;
  };

  const renderPagination = () => {
    if (pagination) {
      return (
        <PaginationComponent
          onChange={onChangePagination}
          pagination={pagination}
          pageSizeOptions={pageSizeOptions}
        />
      );
    }
    return null;
  };

  const renderTableTitle = () => {
    const tableWidth = props.isEnableResize
      ? `${tableInnerRef.current?.getBoundingClientRect().width}px`
      : 'auto';
    return (
      <TableTitleComponent
        title={props.title}
        currentPage={pagination?.current}
        style={{ minWidth: '100%', width: tableWidth }}
      />
    );
  };

  const renderIncompleteMultiRowsHeader = () => {
    return (
      <IncompleteMultiLevelHeaderComponent<T>
        isRowSelection={!!props.rowSelection}
        multiRowsHeader={props.multiRowsHeader}
        enabledCollapse={props.enabledMultiColumnCollapse}
        collapseIndex={multiColumnCollapseIndex}
        onChangedCollapseIndex={setMultiColumnCollapseIndex}
      />
    );
  };

  const renderTableHeader = () => {
    if (props.showHeader !== false) {
      return (
        <TableHeader<T>
          isStickyHeader={props.isStickyHeader}
          rowSelection={props.rowSelection}
          rowKey={props.rowKey || 'id'}
          columns={props.columns}
          data={currentList}
          fullData={sourceList}
          pagination={pagination}
          onHeaderRow={props.onHeaderRow}
          sortValue={sortValue}
          setSortValue={onChangedSortValue}
          sortLoading={sortLoading}
          filterValues={filterValues}
          onChangedFilterValues={onChangedFilterValues}
          children={renderIncompleteMultiRowsHeader()}
          isEnableResize={
            props?.isEnableResize === true ? props.isEnableResize : false
          }
          resizeConfig={props?.resizeConfig || {}}
          onWidthChanged={onWidthChanged}
          columnCollapseIndex={multiColumnCollapseIndex}
          tableRefElement={tableRef.current || undefined}
        />
      );
    }
    return null;
  };

  const renderTableBody = () => {
    if (props.showBody !== false) {
      return <tbody>{renderBodyContent()}</tbody>;
    }
    return null;
  };

  const className = classNames('u-table u-table-hover', {
    [`u-table-${props.size || 'small'}`]: true,
    'u-table-bordered': props.bordered !== false,
    'u-table-borderless': !!props.borderLess,
    'u-table-striped': !!props.striped,
    'u-table-draggable': props?.isEnableResize,
    'u-table-collapse': props.enabledMultiColumnCollapse,
    'auto-table-expand': !!props.expandDetail,
  });

  const style: React.CSSProperties = Object.assign(
    {},
    getTableScrollStyle(props.scroll),
    props.style,
    getDragEnterStyle()
  );

  return (
    <Spin spinning={props.loading}>
      <div
        ref={tableRef}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        style={style}
        className={className}
      >
        {renderTableTitle()}
        <table ref={tableInnerRef}>
          {renderTableHeader()}
          {renderTableBody()}
        </table>
        {renderFooter()}
      </div>
      {renderPagination()}
    </Spin>
  );
}
